import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebaseConfig';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { showToast } from '../components/Toast';
import EventModal from './EventModal';
import EventDetailModal from './EventDetailModal';
import '../css/events.css';

function Events({ userRole }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, upcoming, past

  // States for the gallery
  const [galleries, setGalleries] = useState([]);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);



  // Load events from Firestore
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'events'), orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      const eventsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEvents(eventsData);
    } catch (error) {
      console.error('❌ Error loading events:', error);
      showToast('Error loading events', 'error', true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (event = null) => {
    setEditingEvent(event);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEvent(null);
  };

  const getEventTypeLabel = (type) => {
    const types = {
      concert: '🎤 Concert',
      festival: '🎵 Festival',
      workshop: '🎓 Workshop',
      other: '📌 Other',
    };
    return types[type] || type;
  };

  const isEventPast = (date) => {
    return new Date(date) < new Date();
  };

  const filteredEvents = events.filter((event) => {
    if (filterType === 'upcoming') {
      return !isEventPast(event.date);
    } else if (filterType === 'past') {
      return isEventPast(event.date);
    }
    return true;
  });

  const isAdminOrSuperAdmin = userRole === 'admin' || userRole === 'superadmin';

  // Functions for the gallery
  const loadGalleries = async () => {
    try {
      const q = query(collection(db, 'generalGallery'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const galleriesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGalleries(galleriesData);
    } catch (error) {
      console.error('❌ Error loading galleries:', error);
      showToast('Error loading galleries', 'error', true);
    }
  };

  useEffect(() => {
    loadGalleries();
  }, []);

  const handleOpenGalleryModal = () => {
    setGalleryImages([]);
    setShowGalleryModal(true);
  };

  const handleCloseGalleryModal = () => {
    setShowGalleryModal(false);
    setGalleryImages([]);
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        showToast('Please select images only', 'error', true);
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showToast('Some images exceed 5MB', 'error', true);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setGalleryImages((prev) => [...prev, {
          preview: event.target.result,
          file: file,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadGallery = async () => {
    if (galleryImages.length === 0) {
      showToast('Select at least one image', 'error', true);
      return;
    }

    setUploadingGallery(true);

    try {
      const uploadedImages = [];

      for (const img of galleryImages) {
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(7);
        const fileName = `galleries/general/${timestamp}-${randomId}.jpg`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, img.file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedImages.push(downloadURL);
      }

      await addDoc(collection(db, 'generalGallery'), {
        images: uploadedImages,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid,
      });

      showToast('✅ Images uploaded successfully', 'success', true);
      await loadGalleries();
      handleCloseGalleryModal();
    } catch (error) {
      console.error('❌ Error uploading images:', error);
      showToast('Error uploading images: ' + error.message, 'error', true);
    } finally {
      setUploadingGallery(false);
    }
  };

  const deleteGallery = async (galleryId) => {
    if (window.confirm('Are you sure you want to delete this gallery?')) {
      try {
        await deleteDoc(doc(db, 'generalGallery', galleryId));
        showToast('✅ Gallery deleted successfully', 'success', true);
        await loadGalleries();
      } catch (error) {
      console.error('❌ Error deleting gallery:', error);
      showToast('Error deleting gallery', 'error', true);
      }
    }
  };

  return (
    <div className="events-page-wrapper">
      <div className="events-container">
        {/* Admin Panel */}
        {isAdminOrSuperAdmin && (
          <div className="admin-panel-events">
            <div className="admin-controls">
              <button className="btn-create-event" onClick={() => handleOpenModal()}>
                <span className="icon">➕</span> Create New Event
              </button>
              <button className="btn-add-gallery" onClick={() => handleOpenGalleryModal()}>
                <span className="icon">📸</span> Add Image to Gallery
              </button>
            </div>
          </div>
        )}

        {/* Filter Buttons */}
        <div className="events-filter">
          <button
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filterType === 'upcoming' ? 'active' : ''}`}
            onClick={() => setFilterType('upcoming')}
          >
            Upcoming
          </button>
          <button
            className={`filter-btn ${filterType === 'past' ? 'active' : ''}`}
            onClick={() => setFilterType('past')}
          >
            Past
          </button>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="empty-state">
            <h2>📭 No Events</h2>
            <p>
              {filterType === 'upcoming'
                ? 'No upcoming events at the moment'
                : filterType === 'past'
                ? 'No past events'
                : 'No events available'}
            </p>
          </div>
        ) : (
          <div className="events-grid">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className={`event-card ${isEventPast(event.date) ? 'past' : 'upcoming'}`}
              >
                {/* Event Image */}
                {event.image && (
                  <div className="event-image">
                    <img src={event.image} alt={event.title} />
                    <div className="event-type-badge">{getEventTypeLabel(event.eventType)}</div>
                    {isEventPast(event.date) && <div className="event-status-badge">Past</div>}
                  </div>
                )}

                {/* Event Info */}
                <div className="event-info">
                  <h3 className="event-title">{event.title}</h3>

                  <div className="event-details">
                    <div className="detail-row">
                      <span className="icon">📅</span>
                      <span>{new Date(event.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}</span>
                    </div>

                    <div className="detail-row">
                      <span className="icon">⏰</span>
                      <span>{event.time}</span>
                    </div>

                    <div className="detail-row">
                      <span className="icon">📍</span>
                      <span>{event.location}</span>
                    </div>

                    {event.capacity && (
                      <div className="detail-row">
                        <span className="icon">👥</span>
                        <span>Capacity: {event.capacity}</span>
                      </div>
                    )}

                    {event.ticketPrice && (
                      <div className="detail-row">
                        <span className="icon">💰</span>
                        <span>${event.ticketPrice}</span>
                      </div>
                    )}
                  </div>

                  <p className="event-description">{event.description}</p>

                  {/* Action Buttons */}
                  <div className="event-actions">
                    <button
                      className="btn-view-detail"
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowDetailModal(true);
                      }}
                    >
                      View Details
                    </button>

                    {isAdminOrSuperAdmin && (
                      <>
                        <button
                          className="btn-edit-event"
                          onClick={() => handleOpenModal(event)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete-event"
                          onClick={() => handleOpenModal(event)}
                        >
                          Delete
                        </button>
                      </>
                    )}

                    {event.ticketUrl && (
                      <a
                        href={event.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-buy-tickets"
                      >
                        Buy Tickets
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal for creating/editing event */}
        {showModal && (
          <EventModal 
            onClose={handleCloseModal}
            event={editingEvent}
            onSuccess={loadEvents}
          />
        )}

        {/* Modal para detalles del evento */}
        {showDetailModal && selectedEvent && (
          <EventDetailModal 
            onClose={() => setShowDetailModal(false)}
            event={selectedEvent}
          />
        )}

        {/* Modal for uploading photo gallery */}
        {showGalleryModal && (
          <div className="modal-overlay" onClick={handleCloseGalleryModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📸 Upload Images to Gallery</h2>
                <button className="btn-close" onClick={handleCloseGalleryModal}>✕</button>
              </div>

              <div className="gallery-form">
                <div className="gallery-upload-area">
                  <label htmlFor="gallery-input" className="gallery-upload-label">
                    <span className="upload-icon">📷</span>
                    <span className="upload-text">Select multiple photos</span>
                    <span className="upload-subtext">or drag them here</span>
                  </label>
                  <input
                    id="gallery-input"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleGalleryImageUpload}
                    className="gallery-file-input"
                  />
                </div>

                {/* Gallery Images Preview */}
                {galleryImages.length > 0 && (
                  <div className="gallery-preview-grid">
                    <h3>Selected images ({galleryImages.length})</h3>
                    <div className="preview-grid">
                      {galleryImages.map((img, index) => (
                        <div key={index} className="preview-item">
                          <img src={img.preview} alt={`Preview ${index}`} />
                          <button
                            type="button"
                            className="btn-remove-image"
                            onClick={() => removeGalleryImage(index)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="gallery-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleCloseGalleryModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={handleUploadGallery}
                    disabled={uploadingGallery || galleryImages.length === 0}
                  >
                    {uploadingGallery ? '⏳ Uploading...' : '📤 Upload Images'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Section - Visible para todos con scroll horizontal */}
        {galleries.length > 0 && (
          <div className="gallery-section">
            <div className="galleries-horizontal-container">
              <div className="galleries-scroll">
                {galleries.map((gallery) =>
                  gallery.images.map((image, imgIndex) => (
                    <div key={`${gallery.id}-${imgIndex}`} className="gallery-card-horizontal">
                      <div className="gallery-image-single">
                        <img src={image} alt={`Gallery image ${imgIndex}`} />
                      </div>
                      
                      {/* Only admins see the delete button */}
                      {isAdminOrSuperAdmin && (
                        <button
                          className="btn-delete-image"
                          onClick={() => deleteGallery(gallery.id)}
                          title="Delete this gallery"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Events;